import React, { useCallback, useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";
import { api } from "./api";

/* Réglages › « Gmail » (Jen, 02.11.2026 : « pour que les mails répondus soient marqués comme
   traités … à moins qu'un autre e-mail de suivi soit envoyé et pas répondu »). Un petit script
   Google, collé une fois dans script.google.com avec le compte de l'école : toutes les 10
   minutes, il dit à la console à qui l'école a répondu depuis Gmail (adresses hachées) et,
   pour les familles qui ont écrit par le site, le texte de ces réponses (« oui mets-le »). La
   console marque alors ces messages « Traité » et montre la réponse dessous. */

const quand = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const ETAPES = [
  "Touche « Copier le script » ci-dessous.",
  "Ouvre script.google.com, connectée avec info1000arts@gmail.com, puis « Nouveau projet ».",
  "Efface tout ce qui est écrit, colle le script, puis la disquette 💾 pour enregistrer (nom : « 1000 ARTS – messages »).",
  "En haut, ▶ « Exécuter ». Google demande l'autorisation : « Examiner les autorisations » → le compte de l'école → si « Google n'a pas validé cette application » : « Paramètres avancés » → « Accéder à 1000 ARTS – messages » → « Autoriser ».",
  "À gauche, l'horloge ⏰ « Déclencheurs » → « Ajouter un déclencheur » : fonction « synchroniser », « Déclenché par le temps », « Minuteur », « Toutes les 10 minutes » → « Enregistrer ».",
];

export default function ReglagesGmail({ demander }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [copie, setCopie] = useState(false);
  const charger = useCallback(() => api("/gmail-suivi").then(setD).catch((e) => setErr(e.message)), []);
  useEffect(() => { charger(); }, [charger]);

  const copier = async () => {
    try { await navigator.clipboard.writeText(d.script); setCopie(true); setTimeout(() => setCopie(false), 2500); }
    catch { setErr("Copie impossible ici : sélectionne le texte du script et copie-le à la main."); }
  };
  const nouvelleCle = async () => {
    if (!await demander({ titre: "Changer la clé du script ?", texte: "L'ancien script ne pourra plus rien envoyer.",
      points: ["Il faudra recoller le nouveau script dans script.google.com (étapes 1 à 3)."], valider: "Changer la clé" })) return;
    try { await api("/gmail-suivi/nouvelle-cle", { method: "POST" }); await charger(); } catch (e) { setErr(e.message); }
  };

  if (!d) return <p className="text-sm text-[var(--muted)]">{err || "Chargement…"}</p>;
  const b = d.dernier_bilan || {};
  return (
    <div className="space-y-4" data-testid="reglages-gmail">
      <div className="rounded-2xl px-4 py-3 text-sm flex flex-wrap items-center gap-2"
        style={d.relie ? { background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" } : { background: "#F8FAFC", border: "1px solid var(--border)", color: "#475569" }}
        data-testid="reglages-gmail-etat">
        {d.relie
          ? <><Check size={15} /> <strong>Gmail relié</strong> · dernière vérification le {quand(d.derniere_synchro)}
              {b.traites || b.rouverts ? ` · ${b.traites || 0} marqué(s) traité(s), ${b.rouverts || 0} remis à traiter` : ""}</>
          : <>Pas encore relié : suis les 5 étapes ci-dessous (une seule fois, environ 5 minutes).</>}
      </div>
      <p className="text-sm text-[var(--muted)] leading-relaxed">
        Quand l'école répond à une famille depuis Gmail, son message passe <strong>« Traité »</strong> dans Messages reçus
        (« Répondu depuis Gmail le … »). Si la famille réécrit ensuite sans réponse, il revient <strong>« À traiter »</strong>.
        Ta réponse s'affiche aussi sous le message (objet et texte, sans le message cité), comme une réponse faite
        depuis la console. Le script n'envoie que des dates et des adresses brouillées, et le texte des réponses
        seulement pour les familles qui ont écrit par le site : jamais le reste de ta boîte.
      </p>
      <ol className="text-sm space-y-1.5 list-decimal pl-5" data-testid="reglages-gmail-etapes">
        {ETAPES.map((t) => <li key={t}>{t}</li>)}
      </ol>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-primary text-sm inline-flex items-center gap-1.5" onClick={copier} data-testid="reglages-gmail-copier">
          {copie ? <><Check size={14} /> Copié</> : <><Copy size={14} /> Copier le script</>}
        </button>
        <button type="button" className="btn-ghost text-sm" onClick={nouvelleCle} data-testid="reglages-gmail-cle">Changer la clé</button>
      </div>
      <details className="text-xs">
        <summary className="cursor-pointer text-[var(--muted)]">Voir le script</summary>
        <pre className="mt-2 p-3 rounded-xl overflow-x-auto" style={{ background: "#0F1B2D", color: "#E2E8F0", maxHeight: 260 }}>{d.script}</pre>
      </details>
      {err && <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>}
    </div>
  );
}
